import Phaser from 'phaser';
import { PALETTE } from '../theme';
import type { GameConfig } from '../turn/types';
import { localize, t } from '../i18n';

export class LoadingScene extends Phaser.Scene {
  private config: GameConfig | null = null;

  constructor() {
    super({ key: 'Loading' });
  }

  init(data: { config?: GameConfig }): void {
    this.config = data.config ?? null;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.bg);

    if (!this.config) {
      this.scene.start('MainMenu');
      return;
    }

    // Reset the cross-scene intro handshake for this launch.
    this.game.registry.set('introBegin', null);

    // The intro (scenario name + description) is shown NOW, so the player reads
    // it while TurnGameScene preloads assets and builds the map behind it — no
    // more staring at a blank screen during the heavy load. A plain cover sits
    // under the intro so the map build is never seen.
    this.buildCover();
    this.buildIntro(this.config);

    // Start the game scene on the next frame, so the intro paints before
    // TurnGameScene's synchronous create() can block the main thread.
    const cfg = this.config;
    requestAnimationFrame(() => { this.scene.start('TurnGame', { config: cfg }); });
  }

  /** Plain themed cover that hides the map build behind the intro overlay. */
  private buildCover(): void {
    document.getElementById('scenario-loading-overlay')?.remove();
    const root = document.createElement('div');
    root.id = 'scenario-loading-overlay';
    Object.assign(root.style, {
      position: 'fixed', inset: '0', zIndex: '10',
      background: '#0b0d10 url("wallpaper.png") center/cover no-repeat',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(root);
  }

  /** The single scenario intro screen: name + optional description. Clicking it
   *  starts gameplay as soon as TurnGameScene has finished building (handshake
   *  via the game registry: `introBegin` is set by TurnGameScene when ready). */
  private buildIntro(config: GameConfig): void {
    document.getElementById('scenario-intro-overlay')?.remove();
    if (!document.getElementById('story-overlay-style')) {
      const style = document.createElement('style');
      style.id = 'story-overlay-style';
      style.textContent = `
        @keyframes story-hint-pulse { 0%,100% { opacity: 0.35; } 50% { opacity: 0.9; } }
        @keyframes story-card-in { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `;
      document.head.appendChild(style);
    }

    const sc = config.scenario;
    const title = localize(sc.name);
    const text = sc.story ? localize(sc.story) : '';
    const hasBody = text.trim().length > 0;

    const overlay = document.createElement('div');
    overlay.id = 'scenario-intro-overlay';
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', zIndex: '1000', padding: '24px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(160deg, rgba(8,12,18,0.82), rgba(6,10,16,0.94)), #0b0d10 url("wallpaper.png") center/cover no-repeat',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
    } as Partial<CSSStyleDeclaration>);

    const card = document.createElement('div');
    Object.assign(card.style, {
      maxWidth: '680px', maxHeight: '80vh', overflowY: 'auto', boxSizing: 'border-box',
      padding: '34px 44px',
      background: 'rgba(12,17,25,0.86)',
      border: '1px solid rgba(190,165,110,0.35)',
      borderRadius: '10px',
      boxShadow: '0 18px 60px rgba(0,0,0,0.6), inset 0 0 60px rgba(0,0,0,0.35)',
      animation: 'story-card-in 0.45s ease-out',
    } as Partial<CSSStyleDeclaration>);

    const titleEl = document.createElement('div');
    titleEl.textContent = title;
    Object.assign(titleEl.style, {
      fontFamily: "Georgia, 'Times New Roman', serif",
      color: '#e8dcc2', fontSize: '27px', fontWeight: '700', letterSpacing: '1px',
      textAlign: 'center', lineHeight: '1.25', textShadow: '0 2px 8px rgba(0,0,0,0.6)',
      marginBottom: hasBody ? '14px' : '0',
    } as Partial<CSSStyleDeclaration>);
    card.appendChild(titleEl);

    if (hasBody) {
      const rule = document.createElement('div');
      Object.assign(rule.style, {
        height: '1px', margin: '0 0 22px',
        background: 'linear-gradient(90deg,transparent,rgba(190,165,110,0.55),transparent)',
      } as Partial<CSSStyleDeclaration>);
      card.appendChild(rule);

      const bodyEl = document.createElement('div');
      bodyEl.textContent = text;
      Object.assign(bodyEl.style, {
        fontFamily: "Georgia, 'Times New Roman', serif",
        color: '#e3e8ef', fontSize: '18px', lineHeight: '1.85', textAlign: 'left',
        textShadow: '0 1px 3px rgba(0,0,0,0.5)',
      } as Partial<CSSStyleDeclaration>);
      card.appendChild(bodyEl);
    }

    // Footer: a REAL progress bar driven by the game scene's asset loader, and a
    // start button revealed only once everything is loaded (TurnGameScene emits
    // 'intro-ready' at the end of create()). No fake/timed animation.
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      marginTop: '24px', display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: '10px',
    } as Partial<CSSStyleDeclaration>);

    const barTrack = document.createElement('div');
    Object.assign(barTrack.style, {
      width: '300px', maxWidth: '80vw', height: '8px', background: '#1a1f2a',
      border: '1px solid #2a3140', borderRadius: '4px', overflow: 'hidden',
    } as Partial<CSSStyleDeclaration>);
    const barFill = document.createElement('div');
    Object.assign(barFill.style, {
      width: '0%', height: '100%', background: '#cdb98a', borderRadius: '4px',
      transition: 'width 0.25s ease',
    } as Partial<CSSStyleDeclaration>);
    barTrack.appendChild(barFill);

    const status = document.createElement('div');
    status.textContent = t('loading.preparing');
    Object.assign(status.style, {
      color: '#cdb98a', fontSize: '12px', letterSpacing: '1.5px', textTransform: 'uppercase',
      opacity: '0.85', animation: 'story-hint-pulse 1.8s ease-in-out infinite',
    } as Partial<CSSStyleDeclaration>);

    const startBtn = document.createElement('button');
    startBtn.className = 'rpt-btn';
    startBtn.textContent = t('intro.begin');
    startBtn.style.cssText = "display:none;width:280px;height:46px;border:none;cursor:pointer;background:url('/graphics/ui/confirmation-button-background.png') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s";
    if (!document.getElementById('rpt-btn-style')) {
      const style = document.createElement('style');
      style.id = 'rpt-btn-style';
      style.textContent = '.rpt-btn:hover{filter:brightness(1.2)}.rpt-btn:active{filter:brightness(0.9);transform:scale(0.97)}';
      document.head.appendChild(style);
    }

    footer.appendChild(barTrack);
    footer.appendChild(status);
    footer.appendChild(startBtn);

    overlay.appendChild(card);
    overlay.appendChild(footer);

    const game = this.game;
    const onProgress = (p: number): void => {
      barFill.style.width = `${Math.round(Math.max(0, Math.min(1, p)) * 100)}%`;
    };
    game.events.on('intro-progress', onProgress);
    game.events.once('intro-ready', () => {
      game.events.off('intro-progress', onProgress);
      barFill.style.width = '100%';
      // Swap the bar/status for the start button once fully loaded.
      barTrack.style.display = 'none';
      status.style.display = 'none';
      startBtn.style.display = 'inline-block';
    });

    startBtn.addEventListener('click', () => {
      const begin = game.registry.get('introBegin') as (() => void) | null;
      if (begin) begin();
    });

    document.body.appendChild(overlay);
  }
}
